/* ==========================================================================
   UNICA cart drawer — "Complete Your Look" recommendations (2026-09-21)
   Loaded (deferred) from sections/cart-drawer.liquid only. Plain vanilla JS.

   What it does
   - When the drawer opens (Toggle:CartDrawer:open) it fetches Shopify product
     recommendations for the first line's product through the section rendering
     API (sections/unica-cart-recs.liquid), intent=related then complementary.
   - Results are cached per product id: at most one request per open, and a new
     request only when the first line's product changes.
   - Cards for products that are already in the cart are hidden client-side too.
   - "Add to Cart" goes through window.themeCore.CartApi exactly like the theme's
     own add (ADD_TO_CART_MANY then GET_CART) so the drawer re-renders, the header
     count updates and Shopify's tracking sees a normal add.
   Everything else in the drawer (quantity, remove, note, discount code, free
   shipping bar) is handled by the theme's compiled cart script — see
   qa/cart-drawer-hooks.md.
   ========================================================================== */
(function () {
  'use strict';

  var SEL = {
    drawer: '#CartDrawer',
    recs: '.js-unica-recs',
    recsBody: '.js-unica-recs-body',
    recsInner: '.js-unica-recs-inner',
    card: '.js-unica-rec',
    add: '.js-unica-rec-add',
    addLabel: '.js-unica-rec-add-label',
    select: '.js-unica-rec-select',
    selectLabel: '.js-unica-rec-select-label',
    price: '.js-unica-rec-price',
    error: '.js-unica-rec-error',
    line: '[data-cart-item][data-product-id]',
    preloader: '.js-preloader'
  };

  var cache = {}; // productId -> { html: string|null }
  var inFlight = null; // productId currently being fetched
  var renderedFor = null; // productId the visible cards were rendered for
  var debounceTimer = null;
  var busy = false;

  function ready(fn) {
    if (window.themeCore && window.themeCore.loaded) {
      fn();
    } else {
      document.addEventListener('theme:all:loaded', fn, { once: true });
    }
  }

  function $(sel, root) {
    return (root || document).querySelector(sel);
  }

  function drawer() {
    return $(SEL.drawer);
  }

  function isOpen() {
    var d = drawer();
    return !!(d && d.classList.contains('is-active'));
  }

  function cartProductIds() {
    var d = drawer();
    if (!d) return [];
    return Array.prototype.map.call(d.querySelectorAll(SEL.line), function (el) {
      return String(el.getAttribute('data-product-id'));
    });
  }

  function firstProductId() {
    var ids = cartProductIds();
    return ids.length ? ids[0] : null;
  }

  function recsContainer() {
    return $(SEL.recs);
  }

  function hideRecs() {
    var c = recsContainer();
    if (!c) return;
    c.classList.remove('is-loading');
    c.setAttribute('hidden', '');
    var body = $(SEL.recsBody, c);
    if (body) body.innerHTML = '';
    renderedFor = null;
  }

  function formatMoney(cents, format) {
    try {
      if (window.themeCore && window.themeCore.utils && window.themeCore.utils.formatMoney) {
        return window.themeCore.utils.formatMoney(cents, format || (window.themeCore.objects && window.themeCore.objects.shop.money_format));
      }
    } catch (e) {}
    return (cents / 100).toFixed(2);
  }

  /* ---------------------------------------------------------------------- */
  /* Rendering                                                              */
  /* ---------------------------------------------------------------------- */

  function applyLabels(fragment, container) {
    var addLabel = container.getAttribute('data-add-label') || '';
    var selectLabel = container.getAttribute('data-select-label') || '';
    Array.prototype.forEach.call(fragment.querySelectorAll(SEL.addLabel), function (el) {
      el.textContent = addLabel;
    });
    Array.prototype.forEach.call(fragment.querySelectorAll(SEL.add), function (btn) {
      var title = btn.getAttribute('data-product-title') || '';
      btn.setAttribute('aria-label', addLabel + (title ? ': ' + title : ''));
    });
    Array.prototype.forEach.call(fragment.querySelectorAll(SEL.selectLabel), function (el) {
      el.textContent = selectLabel;
    });
    Array.prototype.forEach.call(fragment.querySelectorAll(SEL.select), function (sel) {
      var title = sel.closest(SEL.card) ? sel.closest(SEL.card).getAttribute('data-product-title') : '';
      sel.setAttribute('aria-label', selectLabel + (title ? ': ' + title : ''));
    });
  }

  function filterInCart(root) {
    var ids = cartProductIds();
    var visible = 0;
    Array.prototype.forEach.call(root.querySelectorAll(SEL.card), function (card) {
      var inCart = ids.indexOf(String(card.getAttribute('data-product-id'))) !== -1;
      card.classList.toggle('is-added', inCart);
      if (!inCart) visible += 1;
    });
    return visible;
  }

  function render(productId) {
    var container = recsContainer();
    if (!container) return;
    var entry = cache[productId];
    var body = $(SEL.recsBody, container);
    if (!entry || !entry.html || !body) {
      hideRecs();
      return;
    }
    var doc = new DOMParser().parseFromString(entry.html, 'text/html');
    var inner = doc.querySelector(SEL.recsInner);
    if (!inner) {
      hideRecs();
      return;
    }
    applyLabels(inner, container);
    var visible = filterInCart(inner);
    if (!visible) {
      hideRecs();
      return;
    }
    body.innerHTML = '';
    body.appendChild(document.adoptNode(inner));
    container.removeAttribute('hidden');
    renderedFor = productId;
  }

  /* ---------------------------------------------------------------------- */
  /* Fetching                                                               */
  /* ---------------------------------------------------------------------- */

  function recsUrl(container, productId, intent) {
    var base = container.getAttribute('data-recs-url') || '/recommendations/products';
    var sectionId = container.getAttribute('data-section-id') || 'unica-cart-recs';
    var url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '6');
    url.searchParams.set('intent', intent);
    return url.href;
  }

  function hasCards(html) {
    return typeof html === 'string' && html.indexOf('js-unica-rec"') !== -1;
  }

  function fetchRecs(productId) {
    var container = recsContainer();
    if (!container || !productId) return Promise.resolve();
    if (cache[productId]) {
      render(productId);
      return Promise.resolve();
    }
    if (inFlight === productId) return Promise.resolve();
    inFlight = productId;
    // Reserve the row's height while loading so the content below does not jump.
    if (!renderedFor) {
      container.classList.add('is-loading');
      container.removeAttribute('hidden');
    }

    function get(intent) {
      return fetch(recsUrl(container, productId, intent), { credentials: 'same-origin' }).then(function (res) {
        if (!res.ok) throw new Error('recommendations ' + res.status);
        return res.text();
      });
    }

    var failed = false;
    return get('related')
      .then(function (html) {
        if (hasCards(html)) return html;
        return get('complementary').then(function (html2) {
          return hasCards(html2) ? html2 : null;
        });
      })
      .catch(function () {
        // A 429 / 5xx / offline answer is not "no recommendations": leave the cache
        // empty so the next open tries again.
        failed = true;
        return null;
      })
      .then(function (html) {
        if (!failed) cache[productId] = { html: html };
        inFlight = null;
        container.classList.remove('is-loading');
        // Only paint if this is still the first product and the drawer is open.
        if (isOpen() && firstProductId() === productId) {
          render(productId);
        } else if (!renderedFor) {
          hideRecs();
        }
      });
  }

  function sync() {
    var container = recsContainer();
    if (!container) return;
    var d = drawer();
    if (!d || d.classList.contains('is-empty')) {
      hideRecs();
      return;
    }
    var pid = firstProductId();
    if (!pid) {
      hideRecs();
      return;
    }
    if (renderedFor === pid) {
      // Same seed product: just hide cards that are now in the cart.
      var body = $(SEL.recsBody, container);
      if (body && !filterInCart(body)) hideRecs();
      return;
    }
    if (cache[pid]) {
      render(pid);
      return;
    }
    fetchRecs(pid);
  }

  function scheduleSync(delay) {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      if (isOpen()) sync();
    }, delay || 350);
  }

  /* ---------------------------------------------------------------------- */
  /* Add to cart                                                            */
  /* ---------------------------------------------------------------------- */

  function setPreloader(on) {
    var d = drawer();
    var p = d && d.querySelector(SEL.preloader);
    if (!p) return;
    if (on) {
      p.classList.add('is-active');
    } else {
      setTimeout(function () { p.classList.remove('is-active'); }, 300);
    }
  }

  function errorText(err) {
    if (!err) return 'Something went wrong. Please try again.';
    if (typeof err === 'string') return err;
    return err.description || err.message || 'Something went wrong. Please try again.';
  }

  function onAddClick(event) {
    var btn = event.target.closest(SEL.add);
    if (!btn || !btn.closest(SEL.recs)) return;
    event.preventDefault();
    if (busy) return;
    var api = window.themeCore && window.themeCore.CartApi;
    if (!api) return;

    var card = btn.closest(SEL.card);
    var select = card ? $(SEL.select, card) : null;
    var variantId = select ? select.value : btn.getAttribute('data-variant-id');
    var errorEl = card ? $(SEL.error, card) : null;
    if (!variantId) return;

    busy = true;
    if (errorEl) errorEl.textContent = '';
    btn.classList.add('is-loading');
    btn.setAttribute('disabled', '');
    btn.setAttribute('aria-busy', 'true');
    setPreloader(true);

    var added = false;
    api.makeRequest(api.actions.ADD_TO_CART_MANY, [{ id: Number(variantId), quantity: 1 }], '', { noOpen: true })
      .then(function () {
        added = true;
      })
      .catch(function (err) {
        if (errorEl) errorEl.textContent = errorText(err);
      })
      .then(function () {
        // Same follow-up the theme's own add uses: refreshes the drawer + header count.
        return api.makeRequest(api.actions.GET_CART, { noOpen: true }).catch(function () {});
      })
      .then(function () {
        busy = false;
        setPreloader(false);
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-busy');
        if (added) {
          var label = $(SEL.addLabel, btn);
          var container = recsContainer();
          var addedLabel = container ? container.getAttribute('data-added-label') : '';
          if (label && addedLabel) label.textContent = addedLabel;
          btn.classList.add('is-done');
          // The card is hidden by sync() once the drawer has re-rendered; the disabled
          // button loses focus, so move it to the drawer title (the new line is right below).
          scheduleSync(600);
          setTimeout(function () {
            var title = drawer() && drawer().querySelector('.unica-cart__title');
            if (title && isOpen() && document.activeElement === document.body) {
              // Focusable only for this programmatic move; never a Tab stop.
              title.setAttribute('tabindex', '-1');
              title.addEventListener('blur', function onBlur() {
                title.removeAttribute('tabindex');
                title.removeEventListener('blur', onBlur);
              });
              try { title.focus({ preventScroll: true }); } catch (e) { title.focus(); }
            }
          }, 900);
        } else {
          btn.removeAttribute('disabled');
        }
      });
  }

  function onSelectChange(event) {
    var select = event.target.closest(SEL.select);
    if (!select || !select.closest(SEL.recs)) return;
    var card = select.closest(SEL.card);
    if (!card) return;
    var option = select.options[select.selectedIndex];
    var price = option ? option.getAttribute('data-price') : null;
    var priceEl = $(SEL.price, card);
    if (price && priceEl) {
      priceEl.textContent = formatMoney(Number(price), priceEl.getAttribute('data-money-format'));
    }
    var btn = $(SEL.add, card);
    if (btn) btn.setAttribute('data-variant-id', select.value);
  }

  /* ---------------------------------------------------------------------- */
  /* Scroll position + refresh state                                        */
  /* The compiled script re-emits cart:drawer:open after every quantity, remove,
     note and discount request and that handler scrolls the drawer to the top,
     which hides the row being edited (or the discount error). Remember where
     the shopper was and put them back. It also opens the drawer before the
     content refresh has landed, so a first add briefly shows the empty state:
     hide that while a refresh is pending.                                   */
  /* ---------------------------------------------------------------------- */

  var savedScroll = null;
  var refreshTimer = null;

  function scrollBox() {
    var d = drawer();
    return d && d.querySelector('.js-scroll-lock-container[data-scrollable]');
  }

  function rememberScroll(kind) {
    var sb = scrollBox();
    if (!sb) return;
    savedScroll = { top: sb.scrollTop, at: Date.now(), kind: kind };
  }

  function restoreScroll() {
    if (!savedScroll || Date.now() - savedScroll.at > 8000) return;
    var saved = savedScroll;
    savedScroll = null;
    // The compiled open handler scrolls to the top synchronously; run after it.
    setTimeout(function () {
      var sb = scrollBox();
      if (!sb) return;
      if (saved.kind === 'discount') {
        var block = sb.querySelector('.js-cart-discount-block');
        if (block) {
          try { block.scrollIntoView({ block: 'nearest' }); } catch (e) { sb.scrollTop = saved.top; }
          return;
        }
      }
      sb.scrollTop = saved.top;
    }, 0);
  }

  function setRefreshing(on) {
    var d = drawer();
    if (!d) return;
    clearTimeout(refreshTimer);
    d.classList.toggle('is-refreshing', !!on);
    var p = d.querySelector(SEL.preloader);
    if (p) p.classList.toggle('is-active', !!on);
    if (on) {
      // Safety net: never leave the drawer masked if no refresh ever lands.
      refreshTimer = setTimeout(function () { setRefreshing(false); }, 6000);
    }
  }

  function inDrawer(target) {
    return !!(target && target.closest && target.closest(SEL.drawer));
  }

  /* ---------------------------------------------------------------------- */
  /* Boot                                                                   */
  /* ---------------------------------------------------------------------- */

  ready(function () {
    if (!recsContainer()) return;
    var bus = window.themeCore.EventBus;

    bus.listen('Toggle:CartDrawer:open', function () {
      scheduleSync(50);
    });
    bus.listen('cart:drawer:open', function () {
      restoreScroll();
      scheduleSync(60);
    });
    // After every cart change the compiled script re-renders the drawer; wait for it.
    bus.listen('cart:updated', function () {
      var d = drawer();
      if (d && d.classList.contains('is-empty')) setRefreshing(true);
      scheduleSync(700);
    });

    document.addEventListener('click', onAddClick);
    document.addEventListener('change', onSelectChange);

    // Capture phase: these run before the compiled (delegated) handlers fire the request.
    document.addEventListener('submit', function (event) {
      if (inDrawer(event.target) && event.target.closest('.js-cart-discount-form')) rememberScroll('discount');
    }, true);
    document.addEventListener('change', function (event) {
      var t = event.target;
      if (!inDrawer(t)) return;
      if (t.closest('.js-cart-notes-field') || t.closest('.js-quantity')) rememberScroll('edit');
    }, true);
    document.addEventListener('click', function (event) {
      var t = event.target;
      if (!inDrawer(t) || !t.closest) return;
      if (t.closest('.js-quantity') || t.closest('.js-cart-item-remove') || t.closest('.js-cart-discount-pill-remove')) rememberScroll('edit');
    }, true);

    // The compiled script swaps .js-cart-content's innerHTML after each change and
    // emits nothing afterwards, so watch the swap itself.
    var content = drawer() && drawer().querySelector('.js-cart-content');
    if (content && 'MutationObserver' in window) {
      new MutationObserver(function () {
        setRefreshing(false);
        scheduleSync(80);
      }).observe(content, { childList: true });
    }

    if (isOpen()) scheduleSync(50);
  });
})();
